/**
 * Builds the router from the registry:
 *
 *   /            RequireAuth → DataSourceLayout(live) → DashboardLayout → routes with live: true
 *                (every route on the dev server with VITE_LIVE_PREVIEW=true)
 *   /test        DataSourceLayout(mock, '/test') → DashboardLayout → every route   (flag-gated)
 *   /login       interim sign-in
 *
 * Each tree has its own 404 inside the dashboard frame.
 */
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';

import { env } from '@/config/env';
import { testRoutesEnabled } from '@/data-sources/load';
import { DashboardLayout, type RouteHandle } from '@/layouts/DashboardLayout';
import { DataSourceLayout } from '@/layouts/DataSourceLayout';
import { RequireAuth } from '@/layouts/RequireAuth';
import { requireAuthLoader } from '@/layouts/requireAuthLoader';
import { RootLayout } from '@/layouts/RootLayout';
import LoginPage from '@/pages/Login';

import { RouteError } from './RouteError';
import { APP_ROUTES, NAV_ITEMS, type AppRoute } from './registry';

function toRouteObject(route: AppRoute): RouteObject {
  const handle: RouteHandle = { routeId: route.id };
  const lazy = async () => ({ Component: (await route.page()).default });
  return route.path === '' ? { index: true, handle, lazy } : { path: route.path, handle, lazy };
}

function dashboardChildren(routes: readonly AppRoute[]): RouteObject[] {
  const hasSettings = routes.some((r) => r.id === 'settings');
  return [
    ...routes.map(toRouteObject),
    ...(hasSettings ? [{ path: 'settings', element: <Navigate to="account" replace /> }] : []),
    { path: '*', lazy: async () => ({ Component: (await import('@/pages/NotFound')).default }) },
  ];
}

const visible = (routes: readonly AppRoute[]) =>
  new Set(
    NAV_ITEMS.filter((item) => routes.some((r) => r.navItem === item.id)).map((item) => item.id),
  );

export function buildRouter() {
  const liveRoutes = APP_ROUTES.filter((route) => route.live || env.livePreview);

  const children: RouteObject[] = [
    /*
     * Eager on purpose: the first visit to `/` can redirect here before any
     * source layout mounts. A lazy sibling leaves the router's null hydration
     * fallback on screen while that redirect is resolving in a production
     * build, which can strand an unauthenticated user on a blank `/` page.
     */
    { path: 'login', Component: LoginPage },
    /*
     * Until the first live index page is enabled, `/` has no leaf below the
     * protected layout (a `*` route does not match the empty remainder). Keep
     * the root useful instead of rendering an empty RootLayout outlet.
     */
    ...(!liveRoutes.some((route) => route.path === '')
      ? [{ index: true, element: <Navigate to="/login" replace /> }]
      : []),
    {
      element: <RequireAuth />,
      loader: requireAuthLoader,
      children: [
        {
          element: <DataSourceLayout source="live" basePath="" />,
          errorElement: <RouteError />,
          children: [
            {
              element: <DashboardLayout visibleNavItems={visible(liveRoutes)} />,
              errorElement: <RouteError />,
              children: dashboardChildren(liveRoutes),
            },
          ],
        },
      ],
    },
  ];

  if (testRoutesEnabled) {
    children.push({
      path: 'test',
      element: <DataSourceLayout source="mock" basePath="/test" />,
      errorElement: <RouteError />,
      children: [
        {
          element: <DashboardLayout visibleNavItems={visible(APP_ROUTES)} />,
          errorElement: <RouteError />,
          children: dashboardChildren(APP_ROUTES),
        },
      ],
    });
  }

  return createBrowserRouter([
    {
      path: '/',
      element: <RootLayout />,
      errorElement: <RouteError />,
      children,
    },
  ]);
}
